// KitchenDataContext.js
import React, { createContext, useContext, useReducer } from 'react';

// Initial Data
const initialPatients = [
  { id: 'p1', name: 'Emily Johnson', age: 32, room: '101', condition: 'Diabetes', allergies: ['Lactose'] },
  { id: 'p2', name: 'Michael Chen', age: 28, room: '102', condition: 'Hypertension', allergies: ['Peanuts'] },
  { id: 'p3', name: 'Sarah Williams', age: 45, room: '103', condition: 'Heart Disease', allergies: [] },
  { id: 'p4', name: 'James Martinez', age: 56, room: '104', condition: 'Post-Surgery', allergies: ['Gluten'] },
  { id: 'p5', name: 'Olivia Thompson', age: 29, room: '105', condition: 'Pregnancy', allergies: [] },
  { id: 'p6', name: 'David Wilson', age: 41, room: '106', condition: 'Obesity', allergies: ['Shellfish'] },
  { id: 'p7', name: 'Lisa Anderson', age: 38, room: '107', condition: 'Kidney Disease', allergies: [] },
];

const initialMenuItems = {
  'APJ': {
    code: 'APJ',
    name: 'Apple Juice',
    description: 'Freshly squeezed apple juice',
    ingredients: [
      { name: 'Apples', qty: '3', unit: 'pieces' },
      { name: 'Water', qty: '100', unit: 'ml' },
      { name: 'Honey', qty: '1', unit: 'tsp' }
    ],
    nutrition: { calories: 120, protein: 0.5, carbs: 28, fat: 0.3 },
    category: 'Beverages'
  },
  'CHW': {
    code: 'CHW',
    name: 'Chicken Wrap',
    description: 'Grilled chicken wrap with fresh vegetables',
    ingredients: [
      { name: 'Chicken Breast', qty: '150', unit: 'g' },
      { name: 'Whole Wheat Wrap', qty: '1', unit: 'pieces' },
      { name: 'Mixed Greens', qty: '50', unit: 'g' },
      { name: 'Cherry Tomatoes', qty: '5', unit: 'pieces' },
      { name: 'Cucumber', qty: '0.25', unit: 'pieces' },
      { name: 'Hummus', qty: '2', unit: 'tbsp' }
    ],
    nutrition: { calories: 380, protein: 30, carbs: 40, fat: 10 },
    category: 'Main Course'
  },
  'QNO': {
    code: 'QNO',
    name: 'Quinoa Bowl',
    description: 'Protein-packed quinoa bowl with vegetables',
    ingredients: [
      { name: 'Quinoa', qty: '150', unit: 'g' },
      { name: 'Mixed Vegetables', qty: '200', unit: 'g' },
      { name: 'Avocado', qty: '0.5', unit: 'pieces' },
      { name: 'Lime Juice', qty: '1', unit: 'tsp' },
      { name: 'Olive Oil', qty: '1', unit: 'tbsp' }
    ],
    nutrition: { calories: 350, protein: 12, carbs: 45, fat: 14 },
    category: 'Main Course'
  },
  'GRS': {
    code: 'GRS',
    name: 'Greek Salad',
    description: 'Fresh Greek salad with feta cheese',
    ingredients: [
      { name: 'Cucumber', qty: '1', unit: 'pieces' },
      { name: 'Tomatoes', qty: '2', unit: 'pieces' },
      { name: 'Feta Cheese', qty: '100', unit: 'g' },
      { name: 'Olives', qty: '10', unit: 'pieces' },
      { name: 'Red Onion', qty: '0.25', unit: 'pieces' },
      { name: 'Olive Oil', qty: '2', unit: 'tbsp' }
    ],
    nutrition: { calories: 220, protein: 8, carbs: 12, fat: 18 },
    category: 'Salads'
  },
  'VSS': {
    code: 'VSS',
    name: 'Vegetable Soup',
    description: 'Hearty vegetable soup with herbs',
    ingredients: [
      { name: 'Mixed Vegetables', qty: '300', unit: 'g' },
      { name: 'Vegetable Broth', qty: '500', unit: 'ml' },
      { name: 'Herbs', qty: '1', unit: 'tsp' }
    ],
    nutrition: { calories: 80, protein: 3, carbs: 15, fat: 1 },
    category: 'Soups'
  },
  'SMT': {
    code: 'SMT',
    name: 'Berry Smoothie',
    description: 'Mixed berry protein smoothie',
    ingredients: [
      { name: 'Mixed Berries', qty: '150', unit: 'g' },
      { name: 'Banana', qty: '1', unit: 'pieces' },
      { name: 'Greek Yogurt', qty: '150', unit: 'g' },
      { name: 'Honey', qty: '1', unit: 'tbsp' }
    ],
    nutrition: { calories: 180, protein: 12, carbs: 35, fat: 2 },
    category: 'Beverages'
  },
  'OME': {
    code: 'OME',
    name: 'Vegetable Omelette',
    description: 'Egg white omelette with fresh vegetables',
    ingredients: [
      { name: 'Egg Whites', qty: '4', unit: 'pieces' },
      { name: 'Spinach', qty: '50', unit: 'g' },
      { name: 'Bell Peppers', qty: '1', unit: 'pieces' },
      { name: 'Mushrooms', qty: '50', unit: 'g' }
    ],
    nutrition: { calories: 150, protein: 18, carbs: 8, fat: 3 },
    category: 'Main Course'
  }
};

// Reducer for data management
const dataReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_MENU_ITEM':
      return {
        ...state,
        menuItems: {
          ...state.menuItems,
          [action.payload.code]: action.payload
        }
      };
    
    case 'UPDATE_MENU_ITEM':
      return {
        ...state,
        menuItems: {
          ...state.menuItems,
          [action.payload.code]: action.payload
        }
      };
    
    case 'DELETE_MENU_ITEM':
      const { [action.payload]: deleted, ...remainingItems } = state.menuItems;
      return {
        ...state,
        menuItems: remainingItems
      };
    
    case 'ADD_DIET_PLAN':
      return {
        ...state,
        dietPlans: [...state.dietPlans, action.payload]
      };
    
    case 'UPDATE_DIET_PLAN':
      return {
        ...state,
        dietPlans: state.dietPlans.map(plan => 
          plan.id === action.payload.id ? action.payload : plan
        )
      };
    
    case 'DELETE_DIET_PLAN':
      return {
        ...state,
        dietPlans: state.dietPlans.filter(plan => plan.id !== action.payload)
      };
    
    case 'UPDATE_SCHEDULE':
      return {
        ...state,
        schedules: {
          ...state.schedules,
          [action.payload.date]: action.payload.schedule
        }
      };
    
    default:
      return state;
  }
};

// Initial state
const initialState = {
  patients: initialPatients,
  menuItems: initialMenuItems,
  dietPlans: [],
  schedules: {}
};

// Context
const KitchenDataContext = createContext();

// Provider component
export const KitchenDataProvider = ({ children }) => {
  const [state, dispatch] = useReducer(dataReducer, initialState);

  // Helper functions
  const addMenuItem = (menuItem) => {
    dispatch({ type: 'ADD_MENU_ITEM', payload: menuItem });
  };

  const updateMenuItem = (menuItem) => {
    dispatch({ type: 'UPDATE_MENU_ITEM', payload: menuItem });
  };

  const deleteMenuItem = (code) => {
    dispatch({ type: 'DELETE_MENU_ITEM', payload: code });
  };

  const addDietPlan = (dietPlan) => {
    const newPlan = {
      ...dietPlan,
      id: `dp_${Date.now()}`,
      createdAt: new Date().toISOString()
    };
    dispatch({ type: 'ADD_DIET_PLAN', payload: newPlan });
    
    // Update schedule automatically
    updateScheduleFromDietPlan(newPlan);
    
    return newPlan;
  };

  const updateDietPlan = (dietPlan) => {
    dispatch({ type: 'UPDATE_DIET_PLAN', payload: dietPlan });
    updateScheduleFromDietPlan(dietPlan);
  };

  const deleteDietPlan = (planId) => {
    dispatch({ type: 'DELETE_DIET_PLAN', payload: planId });
  };

  const updateScheduleFromDietPlan = (dietPlan) => {
    const startDate = new Date(dietPlan.startDate);
    const endDate = new Date(dietPlan.endDate);
    
    // Generate schedule for each day in the diet plan
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split('T')[0];
      const dayNumber = Math.floor((d - startDate) / (1000 * 60 * 60 * 24)) + 1;
      
      const daySchedule = state.schedules[dateStr] || {};
      
      // Add meals for this patient on this day
      dietPlan.meals.forEach(meal => {
        if (meal.scheduleDays.includes(dayNumber)) {
          if (!daySchedule[dietPlan.patientId]) {
            daySchedule[dietPlan.patientId] = {};
          }
          daySchedule[dietPlan.patientId][meal.time] = meal.foodCode;
        }
      });
      
      dispatch({
        type: 'UPDATE_SCHEDULE',
        payload: { date: dateStr, schedule: daySchedule }
      });
    }
  };

  const getPatientById = (patientId) => {
    return state.patients.find(p => p.id === patientId);
  };

  const getMenuItemByCode = (code) => {
    return state.menuItems[code];
  };

  const getDietPlansForPatient = (patientId) => {
    return state.dietPlans.filter(plan => plan.patientId === patientId);
  };

  const getScheduleForDate = (date) => {
    return state.schedules[date] || {};
  };

  const getMealsForDay = (date) => {
    const schedule = getScheduleForDate(date);
    const meals = [];
    const times = ['08:00', '10:30', '13:00', '16:00', '20:00'];
    
    Object.keys(schedule).forEach(patientId => {
      times.forEach(time => {
        const foodCode = schedule[patientId][time];
        if (foodCode) {
          const menuItem = getMenuItemByCode(foodCode);
          if (menuItem) {
            const existingMeal = meals.find(m => m.code === foodCode);
            if (existingMeal) {
              existingMeal.quantity += 1;
            } else {
              meals.push({
                code: foodCode,
                name: menuItem.name,
                description: menuItem.description,
                quantity: 1,
                nutrition: menuItem.nutrition,
                ingredients: menuItem.ingredients
              });
            }
          }
        }
      });
    });
    
    return meals;
  };

  const getIngredientsForDay = (date) => {
    const meals = getMealsForDay(date);
    const ingredientTally = new Map();
    
    meals.forEach(meal => {
      const menuItem = getMenuItemByCode(meal.code);
      if (menuItem && menuItem.ingredients) {
        menuItem.ingredients.forEach(ingredient => {
          const key = `${ingredient.name}|${ingredient.unit}`;
          const currentQty = ingredientTally.get(key) || 0;
          const qty = parseFloat(ingredient.qty) || 1;
          ingredientTally.set(key, currentQty + (qty * meal.quantity));
        });
      }
    });
    
    return Array.from(ingredientTally.entries()).map(([key, totalQty]) => {
      const [name, unit] = key.split('|');
      return { name, qty: `${totalQty} ${unit}` };
    });
  };

  const contextValue = {
    ...state,
    // Menu functions
    addMenuItem,
    updateMenuItem,
    deleteMenuItem,
    getMenuItemByCode,
    // Diet plan functions
    addDietPlan,
    updateDietPlan,
    deleteDietPlan,
    getDietPlansForPatient,
    // Patient functions
    getPatientById,
    // Schedule functions
    getScheduleForDate,
    getMealsForDay,
    getIngredientsForDay,
    // Utility
    updateScheduleFromDietPlan
  };

  return (
    <KitchenDataContext.Provider value={contextValue}>
      {children}
    </KitchenDataContext.Provider>
  );
};

// Custom hook to use the context - THIS WAS MISSING IN THE ORIGINAL
export const useKitchenData = () => {
  const context = useContext(KitchenDataContext);
  if (!context) {
    throw new Error('useKitchenData must be used within a KitchenDataProvider');
  }
  return context;
};